import {useEffect, useRef, useState} from "react";

const BAR_COUNT = 48;
const MIN_BAR_HEIGHT = 2;
const FFT_SIZE = 512;

const createEmptyBars = () => Array.from({length: BAR_COUNT}, () => MIN_BAR_HEIGHT);

const AudioTrackWaveform = ({mediaStreamTrack}) => {
    const [bars, setBars] = useState(createEmptyBars);
    const analyserRef = useRef(null);
    const audioContextRef = useRef(null);
    const sourceRef = useRef(null);
    const animationRef = useRef(null);

    useEffect(() => {
        if (!mediaStreamTrack) {
            return undefined;
        }

        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioContext;

        const stream = new MediaStream([mediaStreamTrack]);
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = FFT_SIZE;
        source.connect(analyser);

        analyserRef.current = analyser;
        sourceRef.current = source;

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        const samplesPerBar = Math.max(1, Math.floor(bufferLength / BAR_COUNT));

        const draw = () => {
            analyser.getByteTimeDomainData(dataArray);
            const updatedBars = [];
            for (let i = 0; i < BAR_COUNT; i += 1) {
                const offset = i * samplesPerBar;
                let sum = 0;
                for (let j = 0; j < samplesPerBar; j += 1) {
                    sum += Math.abs(dataArray[offset + j] - 128);
                }
                const average = sum / samplesPerBar;
                // Scale bars to SVG height units
                const height = Math.max(MIN_BAR_HEIGHT, (average / 128) * 80);
                updatedBars.push(height);
            }
            setBars(updatedBars);
            animationRef.current = requestAnimationFrame(draw);
        };

        if (audioContext.state === "suspended") {
            audioContext.resume().catch(() => {
                // Ignore resume failures; waveform will stay idle until context resumes automatically
            });
        }

        draw();

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            if (sourceRef.current) {
                try {
                    sourceRef.current.disconnect();
                } catch (e) {
                    // ignore cleanup errors
                }
            }
            if (analyserRef.current) {
                try {
                    analyserRef.current.disconnect();
                } catch (e) {
                    // ignore cleanup errors
                }
            }
            if (audioContextRef.current) {
                audioContextRef.current.close().catch(() => {
                    // ignore close errors
                });
            }
            analyserRef.current = null;
            sourceRef.current = null;
            audioContextRef.current = null;
        };
    }, [mediaStreamTrack]);

    return (
        <svg
            width="100%"
            height="70%"
            viewBox="0 0 240 100"
            preserveAspectRatio="none"
            style={{display: "block"}}
        >
            <rect x="0" y="0" width="100%" height="100%" fill="white"/>
            {bars.map((barHeight, idx) => {
                const barWidth = 240 / BAR_COUNT;
                const barX = idx * barWidth;
                const barY = (100 - barHeight) / 2;
                return (
                    <rect
                        key={`wave-bar-${idx}`}
                        x={barX + barWidth * 0.15}
                        y={Math.max(0, barY)}
                        width={barWidth * 0.7}
                        height={Math.min(100, barHeight)}
                        rx={1.5}
                        fill="#A9A9A9"
                    />
                );
            })}
        </svg>
    );
};

export default AudioTrackWaveform;
